import React from "react";
import { X, Table as TableIcon, List, Grid } from "lucide-react";

const Modal = ({ isOpen, onClose, content, title, children }) => {
  // Safety check: ensure the modal is asked to open, and has SOMETHING to show (content OR children)
  if (!isOpen) return null;
  if (!content && !children) return null;

  // --- RENDERER 1: SMART TABLES ---
  // (Your existing robust logic for arrays)
  const renderTable = (data) => {
    if (!data || data.length === 0) return null;
    const headers = Object.keys(data[0]);

    return (
      <div className="overflow-hidden border border-slate-200 rounded-xl my-4 shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider"
                >
                  {header.replace(/_/g, " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {data.map((row, idx) => (
              <tr key={idx} className="hover:bg-slate-50 transition-colors">
                {headers.map((header) => (
                  <td
                    key={header}
                    className="px-4 py-3 text-sm text-slate-700 whitespace-normal"
                  >
                    {header === "goal" || header === "region" ? (
                      <span className="font-semibold text-slate-900">
                        {row[header]}
                      </span>
                    ) : (
                      row[header]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // --- RENDERER 2: CARD GRID ---
  // (Your existing robust logic for nested objects)
  const renderGrid = (data) => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-3">
        {Object.entries(data).map(([key, value]) => (
          <div
            key={key}
            className="bg-slate-50 p-3 rounded-lg border border-slate-100 flex flex-col"
          >
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
              {key.replace(/_/g, " ")}
            </span>
            <span className="text-sm text-slate-800 font-medium">
              {typeof value === "object" ? JSON.stringify(value) : value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // --- MAIN CONTENT LOGIC ---
  const renderContent = (data) => {
    // 1. Arrays (Lists or Tables)
    if (Array.isArray(data)) {
      if (data.length > 0 && typeof data[0] === "object") {
        return renderTable(data);
      }
      return (
        <ul className="space-y-2 my-2">
          {data.map((item, index) => (
            <li
              key={index}
              className="flex items-start gap-2 text-sm text-slate-700"
            >
              <span className="mt-1.5 w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0" />
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    // 2. Objects (Nested Data)
    else if (typeof data === "object" && data !== null) {
      return renderGrid(data);
    }
    // 3. Simple Text
    return (
      <p className="text-slate-700 text-sm leading-relaxed my-1">{data}</p>
    );
  };

  // Determine Title: Use prop 'title' first (for LEAP), fallback to 'content.title' (for Overview)
  const displayTitle = title || (content && content.title);
  const displaySummary = content && content.summary;

  return (
    // Backdrop
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex justify-center items-center p-4"
      onClick={onClose}
    >
      {/* Modal Container */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 bg-white flex justify-between items-start sticky top-0 z-10">
          <div>
            <h3 className="text-xl font-bold text-slate-900">{displayTitle}</h3>
            {displaySummary && (
              <p className="text-sm text-slate-500 mt-1 leading-relaxed max-w-xl">
                {displaySummary}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-slate-50 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="p-8 overflow-y-auto custom-scrollbar">
          {/* SCENARIO A: Custom Children (Used for LEAP Audit Log) 
              If 'children' are passed, we ignore the 'content' object logic and just render the children.
          */}
          {children ? (
            <div>{children}</div>
          ) : (
            /* SCENARIO B: Smart Rendering (Used for Campaign Overview) 
               If no children, we map through the 'content' object using your smart renderers.
            */
            <div className="space-y-8">
              {content &&
                Object.entries(content).map(([key, value]) => {
                  if (key === "title" || key === "summary") return null; // Skip header data

                  const label = key.replace(/_/g, " ");

                  return (
                    <div
                      key={key}
                      className="animate-in slide-in-from-bottom-2 duration-500"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {Array.isArray(value) &&
                        typeof value[0] === "object" ? (
                          <TableIcon size={16} className="text-emerald-600" />
                        ) : typeof value === "object" ? (
                          <Grid size={16} className="text-blue-600" />
                        ) : (
                          <List size={16} className="text-slate-400" />
                        )}

                        <h5 className="font-bold text-sm text-slate-900 uppercase tracking-wider">
                          {label}
                        </h5>
                      </div>

                      <div className="ml-6">{renderContent(value)}</div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Footer Hint */}
        <div className="bg-slate-50 px-6 py-3 border-t border-slate-100 text-center">
          <p className="text-xs text-slate-400 font-medium">
            Press ESC or click outside to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default Modal;
